// useFocusItem — picks the single "if you've got a minute" item for the
// right rail. Blended ranking per the design decisions (2026-07-10):
//   1. Pinned (Focus override) — always first
//   2. High-priority + due soon
//   3. Recently talked about (newest first as a proxy)
//   4. Stale (oldest pending, hasn't been touched)
//
// Returns one item or null. The rail renders it as a calm card, never a list.

import { useQuery } from '@tanstack/react-query';
import { listTasks } from '../api/client';
import type { TaskItem } from '../api/types';

function score(t: TaskItem): number {
  let s = 0;
  if (t.highPriority) s += 50;
  if (t.dueDate) {
    const days = (Date.parse(t.dueDate) - Date.now()) / 86_400_000;
    if (days <= 1) s += 40;
    else if (days <= 3) s += 20;
    else if (days <= 7) s += 10;
  }
  const age = (Date.now() - Date.parse(t.createdAt)) / 86_400_000;
  if (age > 7) s += 5;
  return s;
}

export function useFocusItem(track: string = 'all') {
  const trackParam = track === 'all' ? undefined : track;
  const { data, isLoading } = useQuery({
    queryKey: ['tasks', 'open', track],
    queryFn: () => listTasks({ status: 'open', track: trackParam }),
    staleTime: 2 * 60 * 1000,
    retry: false,
  });

  const tasks = data?.tasks ?? [];

  const pinned = tasks.find((t) => t.priority === 'pinned');
  if (pinned) return { item: pinned, isLoading };

  const ranked = [...tasks].sort((a, b) => score(b) - score(a));
  return { item: ranked[0] ?? null, isLoading };
}
